const SOCIAL_BENEFITS_ID = 1;
const OTHER_STAFF_COSTS_ID = 2;

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object || {}, key);

export const toFieldKey = (accountId) => String(accountId);

export const isEmptyDraftValue = (value) => value == null || (typeof value === "string" && value.trim() === "");

export const normalizeConfirmedAmount = (value) => {
  if (value == null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : value;
};

export const toDraftValue = (value) => {
  const normalized = normalizeConfirmedAmount(value);
  return normalized == null ? "" : String(normalized);
};

export const toApiAmount = (value) => {
  if (isEmptyDraftValue(value)) return null;
  const number = Number(value);
  if (!Number.isFinite(number)) throw new Error("The overhead amount is not a valid number.");
  return number;
};

export const areAmountValuesEquivalent = (left, right) => {
  if (isEmptyDraftValue(left) && isEmptyDraftValue(right)) return true;
  try {
    return toApiAmount(left) === toApiAmount(right);
  } catch (error) {
    return false;
  }
};

export const validateFieldDraft = (field, value) => {
  if (isEmptyDraftValue(value)) return { isValid: true };
  const number = Number(value);
  if (!Number.isFinite(number)) return { isValid: false, message: "The value must be a valid number." };
  if (field.parentAccountId === SOCIAL_BENEFITS_ID && number < 0) return { isValid: false, message: "The value must be zero or greater." };
  if (field.kind === "parent" && number < 0) return { isValid: false, message: "The value must be zero or greater." };
  if (field.kind === "child" && field.parentAccountId === OTHER_STAFF_COSTS_ID && number < 0)
    return { isValid: false, message: "The value must be zero or greater." };
  return { isValid: true };
};

const stripChildAmount = (child = {}) => {
  const { amount, ...metadata } = child;
  return metadata;
};

const stripParentAmounts = (parent = {}) => {
  const { amount, total, content, ...metadata } = parent;
  return { ...metadata, content: (content || []).map(stripChildAmount) };
};

export const createOverheadsSnapshot = (overheads = []) => {
  const structure = (overheads || []).map(stripParentAmounts);
  const fields = {};
  const fieldOrder = [];

  (overheads || []).forEach((parent) => {
    const parentKey = toFieldKey(parent.id);
    const parentConfirmed = normalizeConfirmedAmount(parent.amount);
    fields[parentKey] = {
      key: parentKey,
      accountId: parent.id,
      parentAccountId: parent.id,
      kind: "parent",
      label: parent.title,
      required: true,
      draftValue: toDraftValue(parentConfirmed),
      confirmedValue: parentConfirmed,
      revision: 0,
      phase: parentConfirmed == null ? "pristine" : "saved",
      error: null,
      lastSavedAt: null,
    };
    fieldOrder.push(parentKey);

    (parent.content || []).forEach((child) => {
      const childKey = toFieldKey(child.id);
      const childConfirmed = normalizeConfirmedAmount(child.amount);
      fields[childKey] = {
        key: childKey,
        accountId: child.id,
        parentAccountId: parent.id,
        kind: "child",
        label: child.label,
        required: true,
        draftValue: toDraftValue(childConfirmed),
        confirmedValue: childConfirmed,
        revision: 0,
        phase: childConfirmed == null ? "pristine" : "saved",
        error: null,
        lastSavedAt: null,
      };
      fieldOrder.push(childKey);
    });
  });

  return { structure, fields, fieldOrder };
};

export const readServerValues = (overheads = []) => {
  const values = {};
  (overheads || []).forEach((parent) => {
    values[toFieldKey(parent.id)] = normalizeConfirmedAmount(parent.amount);
    (parent.content || []).forEach((child) => {
      values[toFieldKey(child.id)] = normalizeConfirmedAmount(child.amount);
    });
  });
  return values;
};

export const materializeOverheads = ({ structure, fields, selectValue }) =>
  (structure || []).map((parent) => {
    const parentKey = toFieldKey(parent.id);
    const parentField = fields[parentKey];
    const parentAmount = selectValue(parentKey, parentField);

    return {
      ...parent,
      amount: parentAmount,
      total: parentAmount ?? 0,
      content: (parent.content || []).map((child) => {
        const childKey = toFieldKey(child.id);
        const childField = fields[childKey];
        return { ...child, amount: selectValue(childKey, childField) };
      }),
    };
  });

export const buildDraftOverheads = (state) =>
  materializeOverheads({
    structure: state.structure,
    fields: state.fields,
    selectValue: (fieldKey, field) => {
      if (!field) return null;
      try {
        return toApiAmount(field.draftValue);
      } catch (error) {
        return field.draftValue;
      }
    },
  });

export const buildConfirmedOverheads = (state) =>
  materializeOverheads({
    structure: state.structure,
    fields: state.fields,
    selectValue: (fieldKey, field) => field?.confirmedValue ?? null,
  });

export const buildRequestOverheads = (state, includedByField) =>
  materializeOverheads({
    structure: state.structure,
    fields: state.fields,
    selectValue: (fieldKey, field) =>
      hasOwn(includedByField, fieldKey) ? includedByField[fieldKey].submittedApiValue : field?.confirmedValue ?? null,
  });

export const allRequiredFieldsHaveContent = (state) =>
  state.fieldOrder.every((fieldKey) => {
    const field = state.fields[fieldKey];
    return !field.required || !isEmptyDraftValue(field.draftValue);
  });

export const allRequiredFieldsAreValid = (state) =>
  state.fieldOrder.every((fieldKey) => {
    const field = state.fields[fieldKey];
    return !field.required || validateFieldDraft(field, field.draftValue).isValid;
  });

const getNumericDraftOrNull = (field) => {
  try {
    return toApiAmount(field?.draftValue);
  } catch (error) {
    return null;
  }
};

export const getParentFieldKeyForField = (field) => {
  if (!field) return null;
  return toFieldKey(field.kind === "child" ? field.parentAccountId : field.accountId);
};

export const getChildFieldsForParent = (state, parentAccountId) =>
  state.fieldOrder
    .map((fieldKey) => state.fields[fieldKey])
    .filter((field) => field && field.kind === "child" && field.parentAccountId === parentAccountId);

export const validateParentChildrenTotal = (state, parentField) => {
  if (!parentField || parentField.kind !== "parent") return { isValid: true };
  const childFields = getChildFieldsForParent(state, parentField.accountId);
  if (childFields.length === 0) return { isValid: true };
  const parentAmount = getNumericDraftOrNull(parentField);
  if (parentAmount == null) return { isValid: true };
  const childrenTotal = childFields.reduce((sum, child) => {
    const childAmount = getNumericDraftOrNull(child);
    return sum + (childAmount ?? 0);
  }, 0);
  if (childrenTotal <= parentAmount) return { isValid: true };
  return {
    isValid: false,
    parentFieldKey: parentField.key,
    parentAccountId: parentField.accountId,
    totalName: parentField.label,
    totalValue: parentAmount,
    summedValue: childrenTotal,
  };
};

export const validateRelatedParentChildrenTotal = (state, field) => {
  const parentFieldKey = getParentFieldKeyForField(field);
  if (!parentFieldKey) return { isValid: true };
  return validateParentChildrenTotal(state, state.fields[parentFieldKey]);
};

export const buildParentChildrenTotalErrors = (state) => {
  const errors = {};
  state.fieldOrder.forEach((fieldKey) => {
    const field = state.fields[fieldKey];
    if (!field || field.kind !== "parent") return;
    const validation = validateParentChildrenTotal(state, field);
    if (!validation.isValid) errors[fieldKey] = validation;
  });
  return errors;
};

export const allParentChildrenTotalsAreValid = (state) => Object.keys(buildParentChildrenTotalErrors(state)).length === 0;
